/**
 * Strat CMS shared contracts. One source of truth for the Worker, the SDK, the CLI, the MCP server
 * and the web UI. Every request body is validated with these schemas on the server, and every
 * response is validated with them again in the SDK, so a drifting server is caught at the edge.
 */
import { z } from "zod";

export const API_VERSION = "v1";
export const API_PREFIX = "/api/v1";

/* ------------------------------------------------------------------ ids and primitives */

const idOf = (prefix: string) =>
  z.string().regex(new RegExp(`^${prefix}_[0-9A-Za-z]{10,40}$`), `must be a ${prefix}_ id`);

export const Ids = {
  item: idOf("cnt"),
  version: idOf("ver"),
  approval: idOf("apr"),
  schedule: idOf("sch"),
  publication: idOf("pub"),
  asset: idOf("ast"),
  comment: idOf("cmt"),
  event: idOf("evt"),
  user: idOf("usr"),
  tenant: idOf("ten"),
} as const;

export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, "must be a lowercase sha256 hex digest");
export const IsoDateTime = z.string().datetime({ offset: true });
export const Slug = z
  .string()
  .min(3)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lower case letters, digits and single dashes");

/* ------------------------------------------------------------------ website media (no R2) */

/**
 * Public media lives in the Strat Edge website's own static assets (strategyedge.co.za/img/...), not in
 * the CMS: R2 is not enabled (Ayanda, 23 Oct 2026). A media field is the path of a file the website
 * already serves. Absolute strategyedge.co.za URLs are accepted and stored as the path, so no other host
 * (SharePoint, a CDN, anyone's server) can ever be referenced.
 */
export const SITE_ORIGINS = ["https://strategyedge.co.za", "https://www.strategyedge.co.za"] as const;
export const SITE_IMAGE_PATH =
  /^\/img\/(?:[A-Za-z0-9][A-Za-z0-9_-]{0,60}\/){0,3}[A-Za-z0-9][A-Za-z0-9_-]{0,100}(?:\.[A-Za-z0-9_-]{1,40}){0,3}\.(?:webp|png|jpe?g|gif|avif)$/i;

/** "/img/x.webp" or "https://(www.)strategyedge.co.za/img/x.webp" gives "/img/x.webp"; anything else gives null. */
export function siteImagePath(input: string): string | null {
  let v = input.trim();
  for (const o of SITE_ORIGINS) if (v.toLowerCase().startsWith(`${o}/`)) v = v.slice(o.length);
  if (v.includes("..") || v.includes("//") || v.includes("\\")) return null;
  return SITE_IMAGE_PATH.test(v) ? v : null;
}

export const SiteImage = z
  .string()
  .max(300)
  .transform((v, ctx) => {
    const p = siteImagePath(v);
    if (!p) {
      ctx.addIssue({ code: "custom", message: "must be an image the website already serves: /img/<file>.webp, .png, .jpg, .gif or .avif on strategyedge.co.za" });
      return z.NEVER;
    }
    return p;
  });

/* ------------------------------------------------------------------ channels */

export const CHANNELS = ["blog", "linkedin", "instagram", "youtube"] as const;
export const Channel = z.enum(CHANNELS);
export type Channel = z.infer<typeof Channel>;

/** The six Strat Edge service pillars, as the website's SERVICES slugs (build.py). Blog posts link to one. */
export const PILLARS = [
  "business-consulting",
  "compliance-accreditation",
  "infrastructure-environment",
  "ai-digitisation",
  "cybersecurity",
  "coaching-change",
] as const;

/** Free topic tag on an item (LinkedIn uses wider ones: who-we-are, company-news...). */
export const Topic = z.string().min(2).max(60).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const Pillar = z.enum(PILLARS);
export type Pillar = z.infer<typeof Pillar>;

/** Blog: the site's article template. `body` is HTML and is sanitised on the server. */
export const BlogPayload = z.object({
  title: z.string().min(3).max(200),
  summary: z.string().min(10).max(400),
  service: Pillar,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD"),
  body: z.string().min(1).max(200_000),
  /** An uploaded asset. Needs R2, which is off: the server refuses it (FEATURE_DISABLED). */
  heroAssetId: Ids.asset.optional(),
  /** An image the website already serves, stored as its path, e.g. /img/cyber-shield.webp. */
  heroImage: SiteImage.optional(),
  heroAlt: z.string().max(300).optional(),
});
export type BlogPayload = z.infer<typeof BlogPayload>;

/** One LinkedIn card as the server's render.mjs understands it (quote, list, cover...). */
export const LinkedInCard = z
  .object({
    layout: z.string().min(1).max(40),
    eyebrow: z.string().max(200).optional(),
    title: z.string().max(400).optional(),
    body: z.string().max(2000).optional(),
    source: z.string().max(400).optional(),
    items: z.array(z.union([z.string().max(600), z.record(z.string(), z.string().max(600))])).max(20).optional(),
  })
  .catchall(z.union([z.string().max(2000), z.number(), z.boolean(), z.array(z.string().max(600)).max(20)]));
export type LinkedInCard = z.infer<typeof LinkedInCard>;

export const LinkedInPayload = z.object({
  text: z.string().min(1).max(3000),
  cards: z.array(LinkedInCard).max(20).default([]),
  documentTitle: z.string().max(200).optional(),
  assetIds: z.array(Ids.asset).max(20).default([]),
});
export type LinkedInPayload = z.infer<typeof LinkedInPayload>;

export const InstagramPayload = z.object({
  caption: z.string().min(1).max(2200),
  assetIds: z.array(Ids.asset).min(1).max(10),
  altText: z.array(z.string().max(1000)).max(10).default([]),
});
export type InstagramPayload = z.infer<typeof InstagramPayload>;

export const YouTubePayload = z.object({
  title: z.string().min(1).max(100),
  description: z.string().max(5000).default(""),
  tags: z.array(z.string().max(100)).max(30).default([]),
  videoAssetId: Ids.asset,
  thumbnailAssetId: Ids.asset.optional(),
  privacy: z.enum(["public", "unlisted", "private"]).default("public"),
});
export type YouTubePayload = z.infer<typeof YouTubePayload>;

export const PAYLOAD_SCHEMAS = {
  blog: BlogPayload,
  linkedin: LinkedInPayload,
  instagram: InstagramPayload,
  youtube: YouTubePayload,
} as const;

export type PayloadFor<C extends Channel> = z.infer<(typeof PAYLOAD_SCHEMAS)[C]>;
export type AnyPayload = BlogPayload | LinkedInPayload | InstagramPayload | YouTubePayload;

export function parsePayload(channel: Channel, payload: unknown) {
  return PAYLOAD_SCHEMAS[channel].safeParse(payload);
}

/** A short human title for any payload (lists, audit lines, Slack cards). */
export function payloadTitle(channel: Channel, p: AnyPayload): string {
  switch (channel) {
    case "blog":
      return (p as BlogPayload).title;
    case "linkedin":
      return firstLine((p as LinkedInPayload).documentTitle || (p as LinkedInPayload).text);
    case "instagram":
      return firstLine((p as InstagramPayload).caption);
    case "youtube":
      return (p as YouTubePayload).title;
  }
}
const firstLine = (s: string) => {
  const l = s.split("\n").find((x) => x.trim()) || s;
  return l.length > 120 ? `${l.slice(0, 117)}...` : l;
};

/* ------------------------------------------------------------------ state machine */

export const ITEM_STATUSES = [
  "draft",
  "in_review",
  "changes_requested",
  "approved",
  "scheduled",
  "publishing",
  "published",
  "failed",
  "archived",
] as const;
export const ItemStatus = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof ItemStatus>;

/**
 * Allowed item status moves. Anything not listed is refused with INVALID_TRANSITION.
 * A new version always returns the item to draft (it needs a fresh approval), except while a
 * publication is in flight, when edits are refused until the publisher reports back.
 */
export const TRANSITIONS: Record<ItemStatus, readonly ItemStatus[]> = {
  draft: ["in_review", "archived"],
  in_review: ["approved", "changes_requested", "draft", "archived"],
  changes_requested: ["draft", "in_review", "archived"],
  approved: ["scheduled", "publishing", "published", "draft", "archived"],
  scheduled: ["publishing", "published", "failed", "approved", "draft", "archived"],
  publishing: ["published", "failed"],
  published: ["draft", "archived"],
  failed: ["publishing", "published", "approved", "draft", "archived"],
  archived: ["draft"],
};

export function canTransition(from: ItemStatus, to: ItemStatus): boolean {
  return from === to || TRANSITIONS[from].includes(to);
}

export const APPROVAL_STATUSES = ["pending", "approved", "denied", "superseded", "withdrawn"] as const;
export const ApprovalStatus = z.enum(APPROVAL_STATUSES);
export const SCHEDULE_STATUSES = ["scheduled", "due", "done", "cancelled", "failed"] as const;
export const ScheduleStatus = z.enum(SCHEDULE_STATUSES);
/** external = handed to an outside scheduler (Zernio) that has not confirmed yet; never re-claimed. */
export const PUBLICATION_STATUSES = ["pending", "claimed", "external", "published", "failed"] as const;
export const PublicationStatus = z.enum(PUBLICATION_STATUSES);

/* ------------------------------------------------------------------ principals, roles, scopes */

export const ROLES = ["viewer", "editor", "approver", "admin"] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const SCOPES = [
  "content:read",
  "content:write",
  "assets:write",
  "approval:request",
  "approval:relay",
  "schedule:write",
  "publish:execute",
  "events:read",
  "audit:read",
  "import:write",
] as const;
export const Scope = z.enum(SCOPES);
export type Scope = z.infer<typeof Scope>;

/** What each human role may do, expressed as scopes so one check covers people and machines. */
export const ROLE_SCOPES: Record<Role, readonly Scope[]> = {
  viewer: ["content:read"],
  editor: ["content:read", "content:write", "assets:write", "approval:request"],
  approver: ["content:read", "content:write", "assets:write", "approval:request", "schedule:write"],
  admin: [
    "content:read",
    "content:write",
    "assets:write",
    "approval:request",
    "schedule:write",
    "events:read",
    "audit:read",
  ],
};

/** Only humans with one of these roles can decide an approval. No scope grants it to a machine. */
export const APPROVER_ROLES: readonly Role[] = ["approver", "admin"];

export const Principal = z.object({
  kind: z.enum(["human", "service"]),
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  email: z.string().optional(),
  role: Role.optional(),
  scopes: z.array(Scope),
});
export type Principal = z.infer<typeof Principal>;

/* ------------------------------------------------------------------ errors and exit codes */

export const ERROR_CODES = [
  "BAD_REQUEST",
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "HUMAN_REQUIRED",
  "SELF_APPROVAL",
  "CSRF_FAILED",
  "NOT_FOUND",
  "CONFLICT",
  "STALE_VERSION",
  "HASH_MISMATCH",
  "INVALID_TRANSITION",
  "NOT_APPROVED",
  "ALREADY_PUBLISHED",
  "IDEMPOTENCY_KEY_REUSED",
  "IDEMPOTENCY_KEY_REQUIRED",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "RATE_LIMITED",
  "PREVIEW_EXPIRED",
  "FEATURE_DISABLED",
  "INTERNAL",
] as const;
export const ErrorCode = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const ErrorBody = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBody>;

export const HTTP_STATUS: Record<ErrorCode, number> = {
  BAD_REQUEST: 400,
  VALIDATION_FAILED: 422,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  HUMAN_REQUIRED: 403,
  SELF_APPROVAL: 403,
  CSRF_FAILED: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  STALE_VERSION: 409,
  HASH_MISMATCH: 409,
  INVALID_TRANSITION: 409,
  NOT_APPROVED: 409,
  ALREADY_PUBLISHED: 409,
  IDEMPOTENCY_KEY_REUSED: 422,
  IDEMPOTENCY_KEY_REQUIRED: 428,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RATE_LIMITED: 429,
  PREVIEW_EXPIRED: 410,
  FEATURE_DISABLED: 501,
  INTERNAL: 500,
};

/** CLI exit codes. Scripts and agents branch on these, so they never change meaning. */
export const EXIT = {
  OK: 0,
  ERROR: 1,
  USAGE: 2,
  AUTH: 3,
  FORBIDDEN: 4,
  NOT_FOUND: 5,
  CONFLICT: 6,
  INVALID: 7,
  RATE_LIMITED: 8,
  UNAVAILABLE: 9,
} as const;

export function exitCodeFor(code: ErrorCode | "NETWORK"): number {
  switch (code) {
    case "UNAUTHENTICATED":
      return EXIT.AUTH;
    case "FORBIDDEN":
    case "HUMAN_REQUIRED":
    case "SELF_APPROVAL":
    case "CSRF_FAILED":
      return EXIT.FORBIDDEN;
    case "NOT_FOUND":
    case "PREVIEW_EXPIRED":
      return EXIT.NOT_FOUND;
    case "CONFLICT":
    case "STALE_VERSION":
    case "HASH_MISMATCH":
    case "INVALID_TRANSITION":
    case "NOT_APPROVED":
    case "ALREADY_PUBLISHED":
      return EXIT.CONFLICT;
    case "BAD_REQUEST":
    case "VALIDATION_FAILED":
    case "IDEMPOTENCY_KEY_REUSED":
    case "IDEMPOTENCY_KEY_REQUIRED":
    case "PAYLOAD_TOO_LARGE":
    case "UNSUPPORTED_MEDIA_TYPE":
      return EXIT.INVALID;
    case "RATE_LIMITED":
      return EXIT.RATE_LIMITED;
    case "NETWORK":
    case "INTERNAL":
    case "FEATURE_DISABLED":
      return EXIT.UNAVAILABLE;
  }
}

/* ------------------------------------------------------------------ resources */

export const ExternalRef = z.object({
  system: z.string().min(1).max(40),
  key: z.string().min(1).max(60),
  value: z.string().max(500),
});
export type ExternalRef = z.infer<typeof ExternalRef>;

export const Version = z.object({
  id: Ids.version,
  itemId: Ids.item,
  number: z.number().int().positive(),
  channel: Channel,
  payload: z.unknown(),
  hash: Sha256Hex,
  note: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: IsoDateTime,
});
export type Version = z.infer<typeof Version>;

/** What a person's approval also does. Only the approval carries it out, as the approver. */
export const OnApprove = z.object({ action: z.literal("publish"), runAt: IsoDateTime.nullable().optional() });
export type OnApprove = z.infer<typeof OnApprove>;

export const Approval = z.object({
  id: Ids.approval,
  itemId: Ids.item,
  versionId: Ids.version,
  versionNumber: z.number().int(),
  versionHash: Sha256Hex,
  status: ApprovalStatus,
  requestedBy: z.string(),
  requestedByName: z.string(),
  requestedAt: IsoDateTime,
  decidedBy: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: IsoDateTime.nullable(),
  decidedVia: z.string().nullable(),
  reason: z.string().nullable(),
  onApprove: OnApprove.nullable().default(null),
});
export type Approval = z.infer<typeof Approval>;

export const Schedule = z.object({
  id: Ids.schedule,
  itemId: Ids.item,
  versionId: Ids.version,
  versionHash: Sha256Hex,
  target: z.string(),
  runAt: IsoDateTime,
  status: ScheduleStatus,
  createdBy: z.string(),
  createdAt: IsoDateTime,
});
export type Schedule = z.infer<typeof Schedule>;

export const Publication = z.object({
  id: Ids.publication,
  itemId: Ids.item,
  versionId: Ids.version,
  versionHash: Sha256Hex,
  channel: Channel,
  target: z.string(),
  status: PublicationStatus,
  externalId: z.string().nullable(),
  permalink: z.string().nullable(),
  error: z.string().nullable(),
  attempts: z.number().int(),
  claimedBy: z.string().nullable(),
  leaseUntil: IsoDateTime.nullable(),
  publishedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type Publication = z.infer<typeof Publication>;

export const Item = z.object({
  id: Ids.item,
  tenantId: z.string(),
  channel: Channel,
  slug: Slug,
  title: z.string(),
  status: ItemStatus,
  topic: Topic.nullable(),
  series: z.string().nullable(),
  latestVersionId: Ids.version.nullable(),
  latestVersionNumber: z.number().int(),
  approvedVersionId: Ids.version.nullable(),
  publishedVersionId: Ids.version.nullable(),
  createdBy: z.string(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  publishedAt: IsoDateTime.nullable(),
  revision: z.number().int(),
});
export type Item = z.infer<typeof Item>;

export const ItemDetail = Item.extend({
  latestVersion: Version.nullable(),
  pendingApproval: Approval.nullable(),
  activeSchedule: Schedule.nullable(),
  externalRefs: z.array(ExternalRef),
  publications: z.array(Publication),
});
export type ItemDetail = z.infer<typeof ItemDetail>;

export const Asset = z.object({
  id: Ids.asset,
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  sha256: Sha256Hex,
  createdBy: z.string(),
  createdAt: IsoDateTime,
});
export type Asset = z.infer<typeof Asset>;

export const Comment = z.object({
  id: Ids.comment,
  itemId: Ids.item,
  versionId: Ids.version.nullable(),
  body: z.string(),
  by: z.string(),
  byName: z.string(),
  via: z.string().nullable(),
  createdAt: IsoDateTime,
  resolvedAt: IsoDateTime.nullable(),
});
export type Comment = z.infer<typeof Comment>;

export const AuditEntry = z.object({
  seq: z.number().int(),
  id: z.string(),
  at: IsoDateTime,
  actorKind: z.enum(["human", "service", "system"]),
  actorId: z.string(),
  actorName: z.string(),
  via: z.string().nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  requestId: z.string().nullable(),
  data: z.unknown(),
  hash: Sha256Hex,
  prevHash: Sha256Hex.nullable(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const EventRecord = z.object({
  seq: z.number().int(),
  id: Ids.event,
  type: z.string(),
  itemId: z.string().nullable(),
  data: z.unknown(),
  at: IsoDateTime,
});
export type EventRecord = z.infer<typeof EventRecord>;

export const HistoryEntry = z.object({
  at: IsoDateTime,
  kind: z.enum(["version", "approval", "decision", "schedule", "publication", "comment", "audit"]),
  title: z.string(),
  by: z.string(),
  via: z.string().nullable(),
  ref: z.string().nullable(),
  detail: z.string().nullable(),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;

/** What the public website reads: one approved, published snapshot per item. Nothing else. */
export const PublishedSnapshot = z.object({
  itemId: Ids.item,
  channel: Channel,
  slug: Slug,
  versionId: Ids.version,
  versionNumber: z.number().int(),
  hash: Sha256Hex,
  publishedAt: IsoDateTime,
  payload: z.unknown(),
});
export type PublishedSnapshot = z.infer<typeof PublishedSnapshot>;

export const Page = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

/* ------------------------------------------------------------------ request bodies */

export const CreateItemBody = z.object({
  channel: Channel,
  slug: Slug,
  topic: Topic.optional(),
  series: z.string().max(200).optional(),
  payload: z.unknown(),
  note: z.string().max(500).optional(),
});
export type CreateItemBody = z.infer<typeof CreateItemBody>;

export const NewVersionBody = z.object({
  /** Optimistic concurrency: the version this edit was made from. */
  baseVersionId: Ids.version,
  payload: z.unknown(),
  note: z.string().max(500).optional(),
});
export type NewVersionBody = z.infer<typeof NewVersionBody>;

export const UpdateItemBody = z
  .object({
    revision: z.number().int(),
    topic: Topic.nullable().optional(),
    series: z.string().max(200).nullable().optional(),
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateItemBody = z.infer<typeof UpdateItemBody>;

export const RequestApprovalBody = z.object({
  versionId: Ids.version,
  versionHash: Sha256Hex,
  message: z.string().max(1000).optional(),
  /** "Approve = post it" (the Slack LinkedIn flow). runAt in the future schedules instead of posting now. */
  onApprove: OnApprove.optional(),
});
export type RequestApprovalBody = z.infer<typeof RequestApprovalBody>;

/** Take back an open approval request (e.g. to re-ask with a different publish intent). The item returns to draft. */
export const WithdrawApprovalBody = z.object({ reason: z.string().max(500).optional() });
export type WithdrawApprovalBody = z.infer<typeof WithdrawApprovalBody>;

export const OnBehalfOf = z.object({ system: z.enum(["slack"]), userId: z.string().regex(/^[UW][A-Z0-9]{6,20}$/) });

export const DecisionBody = z.object({
  decision: z.enum(["approve", "deny"]),
  versionHash: Sha256Hex,
  reason: z.string().max(2000).optional(),
  /** Only for a relay principal with approval:relay (the Slack adapter): the human who clicked. */
  onBehalfOf: OnBehalfOf.optional(),
});
export type DecisionBody = z.infer<typeof DecisionBody>;

export const ScheduleBody = z.object({
  versionId: Ids.version,
  versionHash: Sha256Hex,
  runAt: IsoDateTime,
  target: z.string().min(1).max(80).optional(),
});
export type ScheduleBody = z.infer<typeof ScheduleBody>;

export const PublishBody = z.object({
  versionId: Ids.version,
  versionHash: Sha256Hex,
  target: z.string().min(1).max(80).optional(),
});
export type PublishBody = z.infer<typeof PublishBody>;

export const ClaimBody = z.object({ leaseSeconds: z.number().int().min(30).max(900).default(300) });
export const PublicationResultBody = z.object({
  status: z.enum(["published", "failed", "external"]),
  externalId: z.string().max(200).optional(),
  permalink: z.string().url().max(500).optional(),
  error: z.string().max(2000).optional(),
});
export type PublicationResultBody = z.infer<typeof PublicationResultBody>;

export const CommentBody = z.object({
  body: z.string().min(1).max(4000),
  versionId: Ids.version.optional(),
  onBehalfOf: OnBehalfOf.optional(),
  displayName: z.string().max(120).optional(),
});
export type CommentBody = z.infer<typeof CommentBody>;

export const ExternalRefsBody = z.object({ refs: z.array(ExternalRef).min(1).max(20) });

export const PreviewLinkBody = z.object({ versionId: Ids.version, ttlSeconds: z.number().int().min(60).max(86400).default(3600) });
export const PreviewLink = z.object({ url: z.string(), expiresAt: IsoDateTime });

/** Migration only (import:write): bring existing content in with its real history intact. */
export const ImportItemBody = z.object({
  channel: Channel,
  slug: Slug,
  topic: Topic.optional(),
  series: z.string().max(200).optional(),
  status: z.enum(["draft", "in_review", "changes_requested", "approved", "publishing", "published", "archived"]),
  createdAt: IsoDateTime,
  versions: z
    .array(
      z.object({
        payload: z.unknown(),
        createdAt: IsoDateTime,
        createdByName: z.string().max(120),
        note: z.string().max(500).optional(),
      }),
    )
    .min(1)
    .max(50),
  approval: z
    .object({ versionNumber: z.number().int().positive(), decidedByName: z.string().max(120), decidedAt: IsoDateTime, decision: z.enum(["approve", "deny"]) })
    .optional(),
  publication: z
    .object({
      versionNumber: z.number().int().positive(),
      target: z.string().max(80),
      externalId: z.string().max(200).optional(),
      permalink: z.string().url().max(500).optional(),
      publishedAt: IsoDateTime.optional(),
      scheduledFor: IsoDateTime.optional(),
      state: z.enum(["published", "external"]).default("published"),
    })
    .optional(),
  externalRefs: z.array(ExternalRef).max(40).default([]),
});
export type ImportItemBody = z.infer<typeof ImportItemBody>;

export const ImportResult = z.object({ itemId: Ids.item, created: z.boolean(), versions: z.number().int() });

export const Me = z.object({ principal: Principal, csrfToken: z.string().nullable() });

export const Health = z.object({ ok: z.literal(true), service: z.literal("strat-cms"), version: z.string(), time: IsoDateTime });

export const ListQuery = z.object({
  channel: Channel.optional(),
  status: ItemStatus.optional(),
  q: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListQuery = z.infer<typeof ListQuery>;

/** Default publish targets per channel. */
export const DEFAULT_TARGET: Record<Channel, string> = {
  blog: "website:strategyedge.co.za",
  linkedin: "linkedin:strat-edge-solutions",
  instagram: "instagram:strat-edge-solutions",
  youtube: "youtube:strat-edge-solutions",
};

/** Channels the CMS publishes itself (the website pulls snapshots); others go to an external publisher. */
export const SELF_PUBLISHED: readonly Channel[] = ["blog"];

/* ------------------------------------------------------------------ hashing */

/** Canonical JSON: object keys sorted, no whitespace, undefined dropped. Same bytes on every runtime. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    if (typeof value === "number" && !Number.isFinite(value)) throw new Error("non-finite number in content");
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v === undefined ? null : v)).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`;
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * The hash an approval is bound to. It covers the item, the version number, the channel and the
 * exact payload, so approving V2 can never authorise V3, another item, or edited bytes.
 */
export function versionHash(input: { itemId: string; number: number; channel: Channel; payload: unknown }): Promise<string> {
  return sha256Hex(canonicalJson({ c: input.channel, i: input.itemId, n: input.number, p: input.payload, s: "strat-cms/version/v1" }));
}

/* ------------------------------------------------------------------ response bodies (validated by the SDK) */

export const Responses = {
  me: z.object({ principal: Principal }),
  itemList: Page(Item),
  itemDetail: ItemDetail,
  created: z.object({ item: ItemDetail, sanitized: z.boolean() }),
  versioned: z.object({ item: ItemDetail, sanitized: z.boolean(), unchanged: z.boolean() }),
  versionList: Page(Version),
  version: Version,
  history: z.object({ items: z.array(HistoryEntry) }),
  commentList: Page(Comment),
  comment: Comment,
  approvalRequested: z.object({ approval: Approval, item: ItemDetail, existing: z.boolean() }),
  decided: z.object({ approval: Approval, item: ItemDetail }),
  approvalList: Page(Approval),
  scheduled: z.object({ schedule: Schedule, item: ItemDetail }),
  itemOnly: z.object({ item: ItemDetail }),
  published: z.object({ publication: Publication, item: ItemDetail, existing: z.boolean() }),
  publicationList: Page(Publication),
  claimed: z.object({ publication: Publication, item: Item, version: Version }),
  asset: Asset,
  uploaded: z.object({ asset: Asset, existing: z.boolean() }),
  previewLink: z.object({ url: z.string(), path: z.string(), expiresAt: IsoDateTime }),
  events: Page(EventRecord),
  audit: Page(AuditEntry),
  imported: ImportResult,
  snapshots: Page(PublishedSnapshot),
  snapshot: PublishedSnapshot,
  ok: z.object({ ok: z.literal(true) }),
} as const;
